/**
 * The blog's index, as data.
 *
 * Deliberately free of JSX so it can be imported from Node: `src/seo/routes.ts`
 * builds one sitemap entry and one prerendered HTML file per post from this
 * list, and both `scripts/sitemap.ts` and the Vite plugins read that under
 * Node rather than in a browser. The components themselves are mapped to slugs
 * in `src/pages/BlogPost.tsx`, which is the only place that needs React.
 *
 * Adding a post: append an entry here, add its component to that map, and it
 * appears in the hub, the sitemap and the prerender pass with no other step.
 * The editorial plan these come from is docs/marketing/content-seo.md.
 */

/** One entry in an article's table of contents. */
export interface PostSection {
  /** `id` on the article's `<h2>`, and the fragment the contents link to. */
  id: string;
  /** Contents label. Usually the heading, shortened when the heading is a sentence. */
  label: string;
}

export interface BlogPost {
  /** URL segment under /blog. Never change one after publication. */
  slug: string;
  /**
   * `<title>` and share-card headline. Keep under ~60 characters, keyword
   * first, and no ` — VecTime Cloud` suffix: on an article the brand name
   * costs characters that the keyword needs (content-seo.md, on-page rules).
   */
  title: string;
  /** The `<h1>`. Free to be longer and more opinionated than `title`. */
  headline: string;
  /** meta description, and the blurb on the hub. ~155 characters. */
  description: string;
  /** Publication date, ISO. Shown on the post and used in its JSON-LD. */
  published: string;
  /** Set only when the body has materially changed, not for typo fixes. */
  updated?: string;
  /** Rounded from the drafted word count; an honest hint, not a measurement. */
  readingMinutes: number;
  /** The one keyword this post is written to answer. One post, one target. */
  keyword: string;
  /** Component source, relative to `web/` — its last commit date is `<lastmod>`. */
  source: string;
  /** Key into `AUTHORS`. Every post is signed by a person, never by the company. */
  author: string;
  /**
   * One-word label shown on the hub card and on the share card's top line:
   * `Benchmark`, `Guide`, `API`. A reader scanning the hub should be able to
   * tell a measurement from a how-to without opening either.
   */
  kicker: string;
  /**
   * The post's `<h2>`s, in document order, for the contents block.
   *
   * Listed here rather than scraped from the rendered article: the prerender
   * pass and the feed both read this file under Node, where there is no DOM to
   * walk, and a contents list that can silently disagree with the headings is
   * worse than none. Adding a section means adding an `id` to the heading and a
   * line here — the two live four lines apart in review.
   */
  sections: readonly PostSection[];
  /**
   * Share card, served from `web/public`, 1200×630.
   *
   * Rendered by `node scripts/brand/render.mjs M8 --title="…"`. Left unset
   * while an article has no card of its own, in which case the site-wide
   * `/brand/og-image.png` stands in — see `postShareImage`.
   */
  ogImage?: string;
}

export const BLOG_POSTS: readonly BlogPost[] = [
  {
    slug: 'timesfm-vs-prophet-benchmark',
    title: 'Zero-shot forecasting benchmarked: TimesFM vs Prophet',
    headline: 'We benchmarked TimesFM zero-shot against Prophet. Here is where each one wins.',
    description:
      '300 M4 series, TimesFM 2.5 zero-shot against Prophet and seasonal-naive. Every number, the series each method loses, and one command to rerun it.',
    published: '2026-08-21',
    readingMinutes: 9,
    keyword: 'zero shot time series forecasting',
    source: 'src/pages/blog/TimesfmVsProphet.tsx',
    author: 'pierre',
    kicker: 'Benchmark',
    // node scripts/brand/render.mjs M8 \
    //   --title="Zero-shot forecasting benchmarked: TimesFM vs Prophet" --kicker="Benchmark"
    ogImage: '/brand/og/timesfm-vs-prophet-benchmark.png',
    sections: [
      { id: 'what-we-ran', label: 'What we ran' },
      { id: 'results', label: 'The results' },
      { id: 'where-timesfm-loses', label: 'Where TimesFM loses' },
      { id: 'intervals', label: 'The interval, not the point' },
      { id: 'prophet-mean', label: "Prophet's mean is not its typical" },
      { id: 'when-prophet-wins', label: 'When Prophet is still right' },
      { id: 'what-would-move-these', label: 'What would move these numbers' },
      { id: 'run-it-yourself', label: 'Run it yourself' },
    ],
  },
  {
    slug: 'quantile-forecasting-explained',
    title: 'Quantile forecasting explained: q50 is not the mean',
    headline: 'q50 is not the mean: how to read a probabilistic forecast correctly',
    description:
      'What the nine quantiles in a forecast response actually promise, why the mean sits below the median on a real series, and which line to plan on.',
    published: '2026-08-27',
    readingMinutes: 7,
    keyword: 'quantile forecasting explained',
    source: 'src/pages/blog/QuantileForecastingExplained.tsx',
    author: 'pierre',
    kicker: 'Guide',
    // node scripts/brand/render.mjs M8 \
    //   --title="Quantile forecasting explained: q50 is not the mean" --kicker="Guide"
    ogImage: '/brand/og/quantile-forecasting-explained.png',
    sections: [
      { id: 'what-a-quantile-promises', label: 'What a quantile promises' },
      { id: 'a-real-response', label: 'A real response, read line by line' },
      { id: 'mean-vs-median', label: 'Mean versus median' },
      { id: 'which-line-to-plan-on', label: 'Which line to plan on' },
      { id: 'checking-calibration', label: 'Checking the band is honest' },
      { id: 'when-quantiles-are-not-enough', label: 'When this is the wrong tool' },
    ],
  },
  {
    slug: 'batch-time-series-forecasting-api',
    title: 'Batch forecasting API: 500 SKUs in four requests',
    headline: 'Forecasting 500 SKUs: batching, chunking and the numbers behind it',
    description:
      'Four series in one call cost 2.08 s against 1.53 s for one. How to chunk a catalogue to your plan ceiling, handle ragged histories and stay under the rate limit.',
    published: '2026-08-28',
    readingMinutes: 6,
    keyword: 'batch time series forecasting api',
    source: 'src/pages/blog/BatchForecastingApi.tsx',
    author: 'pierre',
    kicker: 'API',
    // node scripts/brand/render.mjs M8 \
    //   --title="Batch forecasting API: 500 SKUs in four requests" --kicker="API"
    ogImage: '/brand/og/batch-time-series-forecasting-api.png',
    sections: [
      { id: 'why-batch', label: 'Why one request beats five hundred' },
      { id: 'the-measurement', label: 'The measurement' },
      { id: 'chunking', label: 'Chunking to your plan ceiling' },
      { id: 'ragged-histories', label: 'Ragged histories' },
      { id: 'throughput', label: 'Throughput and the rate limit' },
      { id: 'when-not-to-batch', label: 'When not to batch' },
    ],
  },
  {
    slug: 'forecast-api-rate-limits',
    title: 'Forecast API rate limits: handling 429s and quota right',
    headline: 'Handling 429s and quota correctly against a metered forecasting API',
    description:
      'Token buckets, burst sizes and the six headers on every response. A retry loop that respects Retry-After, and the one status code you must never retry.',
    published: '2026-09-01',
    readingMinutes: 6,
    keyword: 'forecast api rate limits',
    source: 'src/pages/blog/ForecastApiRateLimits.tsx',
    author: 'pierre',
    kicker: 'API',
    // node scripts/brand/render.mjs M8 \
    //   --title="Forecast API rate limits: handling 429s and quota right" --kicker="API"
    ogImage: '/brand/og/forecast-api-rate-limits.png',
    sections: [
      { id: 'two-different-limits', label: 'Two limits, two status codes' },
      { id: 'token-bucket', label: 'How the token bucket refills' },
      { id: 'the-headers', label: 'The headers on every response' },
      { id: 'retry-loop', label: 'A retry loop that behaves' },
      { id: 'discover-limits', label: 'Discover limits, do not hard-code them' },
      { id: 'when-retrying-is-wrong', label: 'When retrying is the wrong move' },
    ],
  },
  {
    slug: 'seasonal-naive-baseline',
    title: 'Seasonal naive forecast: the baseline you must beat first',
    headline: "Beat seasonal-naive first. On a third of monthly series, we didn't.",
    description:
      'Repeat last cycle is the hardest cheap baseline there is. Per-series MASE across 300 M4 series shows where a foundation model clears it and where it does not.',
    published: '2026-09-02',
    readingMinutes: 7,
    keyword: 'seasonal naive forecast baseline',
    source: 'src/pages/blog/SeasonalNaiveBaseline.tsx',
    author: 'pierre',
    kicker: 'Benchmark',
    // node scripts/brand/render.mjs M8 \
    //   --title="Seasonal naive forecast: the baseline you must beat first" --kicker="Benchmark"
    ogImage: '/brand/og/seasonal-naive-baseline.png',
    sections: [
      { id: 'what-seasonal-naive-is', label: 'What seasonal-naive is' },
      { id: 'why-mase', label: 'Why MASE is the right ruler' },
      { id: 'per-series', label: 'Per series, not on average' },
      { id: 'where-it-loses', label: 'Where the model loses' },
      { id: 'run-the-baseline', label: 'Run the baseline in ten lines' },
      { id: 'when-naive-is-enough', label: 'When naive is the right answer' },
    ],
  },
];

/** Newest first — the order the hub lists them in. */
export const BLOG_POSTS_BY_DATE: readonly BlogPost[] = [...BLOG_POSTS].sort((a, b) =>
  b.published.localeCompare(a.published),
);

export const BLOG_INDEX_PATH = '/blog';

export function postPath(slug: string): string {
  return `${BLOG_INDEX_PATH}/${slug}`;
}

export function findPost(slug: string | undefined): BlogPost | undefined {
  return slug === undefined ? undefined : BLOG_POSTS.find((post) => post.slug === slug);
}

/** Site-wide share card, used by any post without one of its own. */
export const DEFAULT_SHARE_IMAGE = '/brand/og-image.png';

/** Absolute-path share card for a post. */
export function postShareImage(post: BlogPost): string {
  return post.ogImage ?? DEFAULT_SHARE_IMAGE;
}

/**
 * The posts either side of this one in publication order.
 *
 * `previous` is older and `next` is newer, which is the order a reader works
 * through an archive — not the order the hub lists them in.
 */
export function adjacentPosts(slug: string): { previous?: BlogPost; next?: BlogPost } {
  // BLOG_POSTS_BY_DATE is newest first, so the neighbour at i+1 is the older one.
  const index = BLOG_POSTS_BY_DATE.findIndex((post) => post.slug === slug);
  if (index === -1) return {};
  return {
    previous: BLOG_POSTS_BY_DATE[index + 1],
    next: BLOG_POSTS_BY_DATE[index - 1],
  };
}

/**
 * Other posts worth offering at the foot of this one, newest first.
 *
 * Deliberately not a similarity score. With a handful of articles the newest
 * ones *are* the most relevant, and a fake relatedness ranking over five posts
 * is theatre. Revisit when there are enough posts for it to be a real question
 * — content-seo.md asks every article to link to at least two others, and this
 * is the floor that guarantees it once the archive can supply them.
 */
export function relatedPosts(slug: string, limit = 2): readonly BlogPost[] {
  return BLOG_POSTS_BY_DATE.filter((post) => post.slug !== slug).slice(0, limit);
}

/** `21 August 2026` — spelled out, because 08/09 reads differently per country. */
export function formatPostDate(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}
